/* eslint-disable no-console */
/* Name:    Adios (Adserve Axios Wrapper)
 * Desc:
 *          Wraps axios calls with the necessary token refresh code in
 *          amplify. Also handles automatic redirection to login screen.
 * Usage:
 *          await adios(() => axios.get('https://end.point'))
 */

import Amplify, { Auth } from 'aws-amplify';
import Axios from 'axios';

const redirectSessionKey = 'AdserveRedirect';

const homePage = `${window.origin}/${localStorage.getItem('AdserveHomePage')}`;

export const adios = async (axiosFunc, redirectUrl) => {
    if (redirectUrl === undefined) {
        redirectUrl = loginRedirectUrl;
    }
    try {
        await checkSession();

        localStorage.removeItem(redirectSessionKey);
    } catch (err) {
        console.error(err?.message || err);
        // Redirect to login page if session has expired/is invalid
        localStorage.setItem(redirectSessionKey, window.location.href);
        window.location.replace(redirectUrl);
    }
    return await axiosFunc();
};

/**
 * validate & update session variables
 */
export const checkSession = async () => {
    // If refresh token is still valid, update tokens...
    const session = await Auth.currentSession();

    const accessToken = session.getAccessToken().getJwtToken();
    const idToken = session.getIdToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();

    localStorage.setItem('AdserveAccessToken', accessToken);
    localStorage.setItem('AdserveIdToken', idToken);
    localStorage.setItem('AdserveRefreshToken', refreshToken);

    Axios.defaults.headers.Authorization = `Bearer ${idToken}`;
};

/**
 * Refreshes a set of valid tokens. Otherwise, bounce to login page
 * @param {string} redirectUrl - Where to redirect after a successful login
 */
export const refreshTokens = async (redirectUrl) => {
    localStorage.removeItem('AdserveRedirect');
    if (redirectUrl === undefined) {
        redirectUrl = loginRedirectUrl;
    }
    try {
        Amplify.configure({
            Auth: {
                mandatorySignId: true,
                region: 'eu-west-1',
                userPoolId: localStorage.getItem('AdservePoolId'),
                userPoolWebClientId: localStorage.getItem('AdserveAppClientId')
            }
        });
        await checkSession();
    } catch (err) {
        console.error(err || err?.message);
        localStorage.setItem(redirectSessionKey, window.location.href);
        window.location.replace(redirectUrl);
    }
};

/**
 *
 * @param {string} redirectUrl - where to redirect to once logged out.
 */
export const cognitoSignOut = (redirectUrl) => {
    if (redirectUrl === undefined || typeof redirectUrl !== 'string') {
        redirectUrl = loginRedirectUrl;
    }
    localStorage.setItem(redirectSessionKey, window.location.href);
    localStorage.removeItem('AdserveIdToken');
    localStorage.removeItem('AdserveAccessToken');
    localStorage.removeItem('AdserveRefreshToken');
    /* Auth.signOut doesn't delete all the tokens from local storage.
     * So we'll do that manually... */
    const COGNITO_MAGIC = 'CognitoIdentityServiceProvider';
    for (const localStorageKey in localStorage) {
        if (localStorageKey.includes(COGNITO_MAGIC)) {
            localStorage.removeItem(localStorageKey);
        }
    }
    Auth.signOut({ global: true });
    window.location.replace(`${window.origin}/login`);
};

export const loginRedirectUrl = `${window.origin}/login`;

/**
 * Gets the value for a claim from the current user's ID token.
 * @param {string} key - claim key
 */
export const getClaimValue = (key) => {
    if (typeof localStorage.getItem('AdserveIdToken') !== 'string' || typeof key !== 'string') {
        return '';
    }
    const decodedToken = JSON.parse(atob(localStorage.getItem('AdserveIdToken').split('.')[1]));
    return decodedToken[key];
};

/**
 * Extracts the privilege shortcodes from the current user's token.
 * @returns Array
 */
export const getPermissions = () => {
    const perms = getClaimValue('userPermissions');
    const permissionsArray = perms.split(',');
    return permissionsArray;
};

/**
 * Returns true if 1 or more of the passed privileges appear in the current user's claims.
 * @param {*} allowedPrivileges - privilege shortcode or an array of privilege shortcodes
 * @param {*} redirectToHomePageIfUnauthorised
 * @returns
 */
export const isAuthorised = (allowedPrivileges, redirectToHomePageIfUnauthorised = false) => {
    let canAccess = false;
    const perms = getPermissions();
    if (perms.includes('ROLE_ADMIN')) {
        // Only applicable to the Administrator system role
        return true;
    }
    switch (typeof allowedPrivileges) {
        case 'Array':
            for (let i = 0; i < allowedPrivileges.length; i++) {
                const privilege = allowedPrivileges[i];
                if (perms.includes(privilege)) {
                    canAccess = true;
                    break;
                }
            }
            break;
        default:
            if (perms.includes(allowedPrivileges)) {
                canAccess = true;
            }
            break;
    }
    for (let i = 0; i < allowedPrivileges.length; i++) {
        const privilege = allowedPrivileges[i];
        if (perms.includes(privilege)) {
            canAccess = true;
            break;
        }
    }
    if (!canAccess && redirectToHomePageIfUnauthorised) {
        window.location.replace(homePage);
    }
    return canAccess;
};

/**
 * Extracts the module homepages from the current user's token.
 * @returns Array
 */
export const getAuthorisedModules = () => {
    const ms = getClaimValue('authorisedModules');
    const ma = ms.split(',');

    return ma;
};

/**
 * Returns true if 1 or more module of the passed homepages appear in the current user's claims.
 * @param {*} m - module homepage or array of module homepages
 * @returns boolean
 */
export const authorisedForModule = (m) => {
    const ma = getAuthorisedModules();
    let retVal = false;
    if (typeof m === 'string') {
        retVal = ma.some((m2) => m2 === m);
    } else if (Array.isArray(m)) {
        retVal = ma.some((m2) => m.some((m3) => m3 === m2));
    }

    return retVal;
};

/**
 * Restrict module visibility based on role.
 *   - Returns true if authorised
 *   - Returns false and redirects to homepage if not
 */
export const authorisedForThisModule = () => {
    const ma = getAuthorisedModules();

    const retVal = ma.some((m) => window.location.pathname.includes(m));

    if (!retVal) {
        const redirectUrl = getClaimValue('userHomePage') || 'login';
        window.location.href = `${window.origin}/${redirectUrl}`;
    }

    return retVal;
};
